/**
 * 미션 제출 페이지 (캠페인별)
 *
 * URL: /apply/{mallId}/{shopNo}/submit/{campaignId}
 * 선정된 참여자가 콘텐츠를 제출하는 전용 페이지.
 *
 * 인증 플로우:
 * 1. ?code=xxx → password setup code로 비밀번호 설정 후 자동 로그인
 * 2. code 없이 접속 → 비밀번호 로그인
 * 3. "비밀번호를 잊으셨나요?" → 인라인 SMS 인증 후 새 비밀번호 설정
 *
 * 다차수 미션 지원:
 * - campaign.mission_rounds가 있으면 차수별 탭 표시
 * - 각 차수별 상태: 미제출 / 제출완료 / 승인 / 반려
 */
import {
  MyApplicationResponse,
  PublicCampaignResponse,
  MissionSubmissionResponse,
  MissionRound,
  OrderStatusResponse,
  clearPhoneSessionToken,
  isPhoneLoggedIn,
  seedingPublicApi,
} from '@/lib/seedingPublicApi'
import { formatPhoneInput } from '@/lib/phoneUtils'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'

// 공통 컴포넌트 (features/ 디렉토리로 이동됨)
import { Footer } from '@/features/apply/components/Footer'
import { ContactCard } from '@/features/apply/components/ContactCard'
import { MediaViewerModal } from '@/features/apply/components/MediaViewerModal'

// 하위 컴포넌트 (features/ 디렉토리로 이동됨)
import { SetupCodeForm, LoginForm } from '@/features/apply/submit/components/AuthSection'
import { CampaignInfoSection } from '@/features/apply/submit/components/CampaignInfoSection'
import { RoundTabs, type RoundSubmissionStatus } from '@/features/apply/submit/components/RoundTabs'
import { MissionGuideSection } from '@/features/apply/submit/components/MissionGuideSection'
import { SubmissionForm, type UploadingFile } from '@/features/apply/submit/components/SubmissionForm'
import { SubmissionHistory } from '@/features/apply/submit/components/SubmissionHistory'

export const Route = createFileRoute(
  '/apply/$mallId/$shopNo/submit/$campaignId',
)({
  component: SubmitPage,
  validateSearch: (search) => ({
    phone: (search.phone as string) || '',
    code: (search.code as string) || '',
  }),
})

// ==================== 다차수 미션 유틸 ====================

/** 차수의 개방/만료 상태 판별 */
function getRoundStatus(round: MissionRound): 'open' | 'expired' {
  const now = new Date()
  const deadline = new Date(round.deadline_at)
  if (now > deadline) return 'expired'
  return 'open'
}

/** 차수별 제출 상태 판별 */
function getRoundSubmissionStatus(
  roundNumber: number,
  submissions: MissionSubmissionResponse[],
): RoundSubmissionStatus {
  const roundSubmissions = submissions.filter((s) => s.round_number === roundNumber)
  if (roundSubmissions.length === 0) return 'not_submitted'
  const latest = roundSubmissions[roundSubmissions.length - 1]
  if (latest.status === 'approved') return 'approved'
  if (latest.status === 'rejected') return 'rejected'
  return 'submitted'
}

// ==================== 상태 레이블/색상 ====================

// (미사용이지만 호환성을 위해 유지)
const _statusLabel: Record<string, string> = {
  pending: '심사중', rejected: '반려', submitted: '검수대기', approved: '승인',
}
const _statusColor: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800', rejected: 'bg-error/10 text-error',
  submitted: 'bg-primary/10 text-primary', approved: 'bg-green-100 text-green-800',
}

// ==================== 메인 컴포넌트 ====================

/** 컴포넌트 props (short URL 라우트에서도 재사용) */
export interface MissionSubmitContentProps {
  mallId: string
  shopNo: number
  campaignId: string
  phoneFromUrl?: string
  codeFromUrl?: string
  campaignPath?: string
  previewMode?: boolean
  previewCampaign?: PublicCampaignResponse
  previewApplication?: MyApplicationResponse
  forceMobile?: boolean
}

/** 미션 제출 컴포넌트 (라우트 비종속) */
export function MissionSubmitContent({
  mallId,
  shopNo,
  campaignId,
  phoneFromUrl: phoneFromUrlProp = '',
  codeFromUrl: codeFromUrlProp = '',
  campaignPath,
  previewMode = false,
  previewCampaign,
  previewApplication,
  forceMobile = false,
}: MissionSubmitContentProps) {
  const shopNoStr = String(shopNo)
  const phoneFromUrl = phoneFromUrlProp
  const codeFromUrl = codeFromUrlProp
  const navigate = useNavigate()

  // ==================== 핵심 상태 ====================

  const [application, setApplication] = useState<MyApplicationResponse | null>(null)
  const [campaign, setCampaign] = useState<PublicCampaignResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [loggedIn, setLoggedIn] = useState(false)

  // Setup code 모드
  const [setupCodeMode, setSetupCodeMode] = useState(false)
  const [setupCodePhone, setSetupCodePhone] = useState('')
  const [setupCodeCampaignTitle, setSetupCodeCampaignTitle] = useState('')
  const [setupCodeUsedMessage, setSetupCodeUsedMessage] = useState<string | null>(null)

  // 미디어 제출 상태
  const [mediaUrls, setMediaUrls] = useState<string[]>([])
  const [seedingUrl, setSeedingUrl] = useState('')
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([])

  // 수정 모드
  const [editingSubmissionId, setEditingSubmissionId] = useState<string | null>(null)
  const [isEditFormOpen, setIsEditFormOpen] = useState(false)
  const [originalMediaUrls, setOriginalMediaUrls] = useState<string[]>([])
  const [originalSeedingUrl, setOriginalSeedingUrl] = useState('')

  // 다차수 미션
  const [selectedRound, setSelectedRound] = useState<number>(1)

  // 미디어 뷰어 모달
  const [viewingMedia, setViewingMedia] = useState<{ urls: string[]; index: number } | null>(null)

  // 주문 상태
  const [orderStatus, setOrderStatus] = useState<OrderStatusResponse | null>(null)
  const [orderStatusLoading, setOrderStatusLoading] = useState(false)

  // ==================== 파생 데이터 ====================

  const hasRounds = !!(campaign?.mission_rounds && campaign.mission_rounds.length > 0)
  const isMultiRound = !!(campaign?.mission_rounds && campaign.mission_rounds.length > 1)

  // 차수별 상태 정보
  const roundsInfo = useMemo(() => {
    if (!campaign?.mission_rounds || !application) return []
    return campaign.mission_rounds.map((round) => ({
      round,
      status: getRoundStatus(round),
      submissionStatus: getRoundSubmissionStatus(round.round, application.submissions),
      submissions: application.submissions.filter((s) => s.round_number === round.round),
    }))
  }, [campaign?.mission_rounds, application])

  // 현재 선택된 차수 정보
  const currentRoundInfo = useMemo(() => {
    if (!hasRounds) return null
    return roundsInfo.find((r) => r.round.round === selectedRound) ?? null
  }, [hasRounds, roundsInfo, selectedRound])

  // 현재 차수의 제출 유형
  const effectiveSubmissionType = useMemo(() => {
    if (!campaign) return 'both'
    if (currentRoundInfo?.round) return currentRoundInfo.round.mission_type || 'both'
    return 'both'
  }, [campaign, currentRoundInfo])

  // 현재 차수에 제출 폼을 보여줄지 결정
  const canSubmitCurrentRound = useMemo(() => {
    if (!currentRoundInfo) return true
    if (currentRoundInfo.submissionStatus === 'approved') return false
    return true
  }, [currentRoundInfo])

  // 변경 감지
  const mediaChanged = useMemo(() => {
    if (!editingSubmissionId) return true
    if (mediaUrls.length !== originalMediaUrls.length) return true
    return mediaUrls.some((url, i) => url !== originalMediaUrls[i])
  }, [editingSubmissionId, mediaUrls, originalMediaUrls])

  const linkChanged = useMemo(() => {
    if (!editingSubmissionId) return true
    return seedingUrl.trim() !== originalSeedingUrl.trim()
  }, [editingSubmissionId, seedingUrl, originalSeedingUrl])

  // 기존 제출 목록 (선택된 차수만, 승인 우선 정렬)
  const existingSubmissions = useMemo(() => {
    if (!application) return []
    const subs = application.submissions.filter((s) => s.round_number === selectedRound)
    const statusOrder: Record<string, number> = { approved: 0, submitted: 1, rejected: 2 }
    return [...subs].sort((a, b) => {
      const orderA = statusOrder[a.status] ?? 9
      const orderB = statusOrder[b.status] ?? 9
      if (orderA !== orderB) return orderA - orderB
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
  }, [application, selectedRound])

  const hasRejectedInRound = existingSubmissions.some((s) => s.status === 'rejected')

  // cafe24_order 캠페인에서 카페24 ID 미입력이면 미션 영역 잠금
  const isCafe24OrderLocked = !previewMode
    && (!campaign?.delivery_method || campaign?.delivery_method === 'cafe24_order')
    && !application?.cafe24_member_id

  // ==================== 데이터 로드 ====================

  const loadApplicationByPhone = async () => {
    try {
      const appData = await seedingPublicApi.getMyApplicationByCampaignPhone(campaignId)
      setApplication(appData)
    } catch (err: any) {
      if (err.message.includes('로그인') || err.message.includes('401') || err.message.includes('인증')) {
        clearPhoneSessionToken()
        setLoggedIn(false)
      } else if (err.message.includes('404') || err.message.includes('신청 정보')) {
        setApplication(null)
      } else {
        setError(err.message)
      }
    }
  }

  const removeCodeFromUrl = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('phone')
    window.history.replaceState({}, '', url.pathname)
  }

  // 초기 로드
  useEffect(() => {
    if (previewMode) {
      if (previewCampaign) setCampaign(previewCampaign)
      if (previewApplication) setApplication(previewApplication)
      setLoggedIn(true)
      setIsLoading(false)
      return
    }

    const checkAndLoad = async () => {
      try {
        const campaignData = await seedingPublicApi.getCampaign(mallId, shopNo, campaignId)
        setCampaign(campaignData)
        document.title = '미션 제출 | 체험톡톡'
      } catch {
        setError('캠페인 정보를 불러올 수 없어요.')
        setIsLoading(false)
        return
      }

      // Setup code 처리
      if (codeFromUrl) {
        try {
          const result = await seedingPublicApi.verifySetupCode(codeFromUrl)
          if (result.valid) {
            setSetupCodeMode(true)
            setSetupCodePhone(result.phone || '')
            setSetupCodeCampaignTitle(result.campaign_title || '')
            setIsLoading(false)
            return
          } else if (result.reason === 'already_used') {
            setSetupCodeUsedMessage('이미 비밀번호가 설정된 링크입니다. 비밀번호로 로그인해주세요.')
            removeCodeFromUrl()
          } else {
            setSetupCodeUsedMessage('유효하지 않은 링크입니다.')
            removeCodeFromUrl()
          }
        } catch {
          setSetupCodeUsedMessage('링크 확인 중 오류가 발생했어요.')
          removeCodeFromUrl()
        }
      }

      const hasPhoneToken = isPhoneLoggedIn()
      setLoggedIn(hasPhoneToken)
      if (!hasPhoneToken) {
        setIsLoading(false)
        return
      }

      await loadApplicationByPhone()
      setIsLoading(false)
    }

    checkAndLoad()
  }, [mallId, shopNo, campaignId])

  // 현재 활성 차수 자동 선택
  useEffect(() => {
    if (!hasRounds || roundsInfo.length === 0) return
    const actionNeeded = roundsInfo.find(
      (r) => r.submissionStatus === 'not_submitted' || r.submissionStatus === 'rejected',
    )
    if (actionNeeded) {
      setSelectedRound(actionNeeded.round.round)
      return
    }
    const notApprovedRound = roundsInfo.find((r) => r.submissionStatus !== 'approved')
    if (notApprovedRound) {
      setSelectedRound(notApprovedRound.round.round)
      return
    }
    setSelectedRound(roundsInfo[roundsInfo.length - 1].round.round)
  }, [hasRounds, roundsInfo])

  // 수정 모드: 기존 submitted 제출이 있으면 프리필
  useEffect(() => {
    if (!application) return
    const currentSubs = application.submissions.filter((s) => s.round_number === selectedRound)
    const submittedSub = currentSubs.find((s) => s.status === 'submitted')

    if (submittedSub) {
      const urls = submittedSub.media_urls || []
      const link = submittedSub.mission_url || ''
      setEditingSubmissionId(submittedSub.id)
      setMediaUrls(urls)
      setSeedingUrl(link)
      setOriginalMediaUrls(urls)
      setOriginalSeedingUrl(link)
      setUploadingFiles(urls.map((url) => ({
        file: new File([], url.split('/').pop() || 'file'),
        progress: 100,
        url,
      })))
      setIsEditFormOpen(false)
    } else {
      setEditingSubmissionId(null)
      setMediaUrls([])
      setSeedingUrl('')
      setOriginalMediaUrls([])
      setOriginalSeedingUrl('')
      setUploadingFiles([])
    }
  }, [application, selectedRound])

  // 주문 상태 조회
  const checkOrderStatus = useCallback(async () => {
    if (!campaign) return
    // 자체배송은 회원 연동(cafe24_member_id) 없이도 송장 조회, 주문형은 회원 연동이 있어야 조회해요
    const isSelfDelivery = (campaign.delivery_method || 'cafe24_order') === 'self_delivery'
    if (!isSelfDelivery && !application?.cafe24_member_id) return
    setOrderStatusLoading(true)
    try {
      const status = await seedingPublicApi.getOrderStatus(mallId, Number(shopNoStr), campaignId)
      setOrderStatus(status)
    } catch {
      // 조회 실패 시 무시
    } finally {
      setOrderStatusLoading(false)
    }
  }, [campaign, application, mallId, shopNoStr, campaignId])

  useEffect(() => {
    if (!campaign) return
    // 자체배송이면 회원 연동 없이도, 주문형이면 회원 연동됐을 때 송장/주문을 조회해요
    const isSelfDelivery = (campaign.delivery_method || 'cafe24_order') === 'self_delivery'
    if (isSelfDelivery || application?.cafe24_member_id) {
      checkOrderStatus()
    }
  }, [application?.cafe24_member_id, campaign, checkOrderStatus])

  // ==================== 인증 콜백 ====================

  const handleSetupComplete = async () => {
    setSetupCodeMode(false)
    setLoggedIn(true)
    setIsLoading(true)
    removeCodeFromUrl()
    await loadApplicationByPhone()
    setIsLoading(false)
  }

  const handleLoginSuccess = async () => {
    setLoggedIn(true)
    setIsLoading(true)
    await loadApplicationByPhone()
    setIsLoading(false)
  }

  // ==================== 미디어 뷰어 핸들러 ====================

  const handleViewMedia = useCallback((urls: string[], index: number) => {
    setViewingMedia({ urls, index })
  }, [])

  // ==================== 렌더링: 로딩 상태 ====================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-lowest flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-on-surface-variant">로딩 중...</p>
        </div>
      </div>
    )
  }

  // ==================== 렌더링: Setup code 모드 ====================

  if (setupCodeMode) {
    return (
      <SetupCodeForm
        campaign={campaign}
        codeFromUrl={codeFromUrl}
        setupCodePhone={setupCodePhone}
        setupCodeCampaignTitle={setupCodeCampaignTitle}
        onSetupComplete={handleSetupComplete}
      />
    )
  }

  // ==================== 렌더링: 로그인 필요 ====================

  if (!loggedIn) {
    return (
      <LoginForm
        campaign={campaign}
        campaignId={campaignId}
        mallId={mallId}
        shopNo={shopNo}
        error={error}
        setupCodeUsedMessage={setupCodeUsedMessage}
        initialPhone={phoneFromUrl}
        onLoginSuccess={handleLoginSuccess}
      />
    )
  }

  // ==================== 렌더링: 신청 없음 ====================

  if (!application) {
    return (
      <div className="min-h-screen bg-surface-lowest">
        <header className="border-b border-border">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <h1 className="text-xl font-bold text-on-surface">
              {campaign?.title ? `${campaign.title} 미션 관리` : '미션 관리'}
            </h1>
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-4 py-8">
          <div className="p-8 text-center">
            <h2 className="text-lg font-semibold text-on-surface mb-2">신청 내역이 없습니다</h2>
            <p className="text-on-surface-variant mb-6">
              이 캠페인에 신청하지 않았거나, 다른 전화번호로 신청하셨을 수 있습니다.
            </p>
            {campaignPath ? (
              <a href={campaignPath} className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dim transition-colors inline-block">
                캠페인 신청하기
              </a>
            ) : (
              <Link
                to="/apply/$mallId/$shopNo/$campaignId"
                params={{ mallId, shopNo: shopNoStr, campaignId }}
                className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dim transition-colors inline-block"
              >
                캠페인 신청하기
              </Link>
            )}
          </div>
        </main>
      </div>
    )
  }

  // ==================== 렌더링: 미선정 ====================

  if (application.status !== 'selected') {
    return (
      <div className="min-h-screen bg-surface-lowest">
        <header className="border-b border-border">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <h1 className="text-xl font-bold text-on-surface">
              {campaign?.title ? `${campaign.title} 미션 관리` : '미션 관리'}
            </h1>
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-4 py-8">
          <div className="p-8 text-center">
            {(() => {
              if (application.status === 'cancelled') {
                return (
                  <>
                    <h2 className="text-lg font-semibold text-on-surface mb-2">선정이 취소되었습니다</h2>
                    <p className="text-on-surface-variant">자세한 내용은 쇼핑몰에 문의해주세요.</p>
                  </>
                )
              }
              const announceAt = campaign?.selection_announce_at || campaign?.recruit_end_at
              const isBeforeAnnounce = announceAt ? new Date(announceAt) > new Date() : false
              const isPending = application.status === 'pending' && isBeforeAnnounce
              return (
                <>
                  <h2 className="text-lg font-semibold text-on-surface mb-2">
                    {isPending ? '선정 대기중입니다' : '선정되지 않았습니다'}
                  </h2>
                  <p className="text-on-surface-variant">
                    {isPending
                      ? '선정 결과가 나오면 미션을 제출할 수 있습니다.'
                      : '다음 기회에 다시 신청해주세요.'}
                  </p>
                </>
              )
            })()}
          </div>
        </main>
      </div>
    )
  }

  // ==================== 렌더링: 메인 제출 페이지 ====================

  // 마감일 결정 (기한 후 제출 표시용)
  const deadlineStr = currentRoundInfo
    ? currentRoundInfo.round.deadline_at
    : campaign?.mission_deadline_at || null

  return (
    <div className="min-h-screen bg-surface-lowest flex flex-col">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <h1 className="text-xl font-bold text-on-surface flex items-center gap-2 flex-wrap">
            {campaign?.title ? `${campaign.title} 미션 관리` : '미션 관리'}
            {campaign?.shop_name && (
              <span className="text-sm font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                {campaign.shop_name}
              </span>
            )}
          </h1>
        </div>
      </header>

      <main className="flex-1">
        {error && (
          <div className="max-w-6xl mx-auto px-4">
            <div className="p-4 bg-error/10 border border-error/30 rounded-lg text-error">{error}</div>
          </div>
        )}

        <div className="max-w-6xl mx-auto px-4 space-y-0">
          {/* 캠페인 정보 + 계정 연동 */}
          <CampaignInfoSection
            campaign={campaign!}
            application={application}
            mallId={mallId}
            shopNo={shopNo}
            campaignId={campaignId}
            previewMode={previewMode}
            forceMobile={forceMobile}
            orderStatus={orderStatus}
            orderStatusLoading={orderStatusLoading}
            onApplicationUpdate={setApplication}
          />

          {/* 다차수 미션 탭 */}
          {isMultiRound && roundsInfo.length > 0 && (
            <RoundTabs
              roundsInfo={roundsInfo}
              selectedRound={selectedRound}
              forceMobile={forceMobile}
              onSelectRound={(round) => {
                setSelectedRound(round)
              }}
            />
          )}

          {/* 2컬럼 그리드: 가이드(좌) + 제출 영역(우) */}
          <div className="relative">
            {/* 카페24 ID 미입력 시 잠금 오버레이 */}
            {isCafe24OrderLocked && (
              <div className="absolute inset-0 z-10 flex items-start justify-center pt-12 pointer-events-none">
                <div className="bg-surface-lowest/95 border border-amber-200 rounded-lg p-5 shadow-lg text-center max-w-sm mx-4">
                  <p className="text-base font-semibold text-amber-800 mb-1">
                    먼저 {campaign?.shop_name || '쇼핑몰'} 아이디를 입력해주세요
                  </p>
                  <p className="text-sm text-amber-700">
                    위에서 아이디를 입력하면{campaign?.auto_coupon ? ' 쿠폰이 발급되고' : ''} 미션을 진행할 수 있어요.
                  </p>
                </div>
              </div>
            )}

            <div className={forceMobile ? '' : 'md:grid md:grid-cols-[2fr_1fr] md:gap-6'}>
              {/* 왼쪽: 미션 가이드 (카페24 ID 미입력 시 잠금) */}
              <div className={isCafe24OrderLocked ? 'opacity-30 pointer-events-none select-none blur-[1px]' : ''}>
                <MissionGuideSection
                  campaign={campaign!}
                  currentRound={currentRoundInfo?.round ?? (hasRounds ? campaign!.mission_rounds![0] : null)}
                  currentSubmissionStatus={currentRoundInfo?.submissionStatus ?? null}
                  isMultiRound={isMultiRound}
                  forceMobile={forceMobile}
                />
              </div>

              {/* 오른쪽: 제출 폼 + 제출 내역 + 문의처 */}
              <div>
                {/* 제출 영역 (카페24 ID 미입력 시 잠금) */}
                <div className={isCafe24OrderLocked ? 'opacity-30 pointer-events-none select-none blur-[1px]' : ''}>
                {/* 신규 제출 / 재제출 폼 */}
                {canSubmitCurrentRound && !editingSubmissionId && (
                  <SubmissionForm
                    application={application}
                    campaignId={campaignId}
                    selectedRound={selectedRound}
                    effectiveSubmissionType={effectiveSubmissionType}
                    hasRejectedInRound={hasRejectedInRound}
                    previewMode={previewMode}
                    mediaUrls={mediaUrls}
                    setMediaUrls={setMediaUrls}
                    uploadingFiles={uploadingFiles}
                    setUploadingFiles={setUploadingFiles}
                    seedingUrl={seedingUrl}
                    setSeedingUrl={setSeedingUrl}
                    mediaChanged={mediaChanged}
                    linkChanged={linkChanged}
                    onViewMedia={handleViewMedia}
                    onApplicationUpdate={setApplication}
                  />
                )}

                {/* 기존 제출 내역 */}
                <SubmissionHistory
                  application={application}
                  campaignId={campaignId}
                  submissions={existingSubmissions}
                  effectiveSubmissionType={effectiveSubmissionType}
                  previewMode={previewMode}
                  deadlineStr={deadlineStr}
                  editingSubmissionId={editingSubmissionId}
                  isEditFormOpen={isEditFormOpen}
                  mediaUrls={mediaUrls}
                  seedingUrl={seedingUrl}
                  uploadingFiles={uploadingFiles}
                  originalMediaUrls={originalMediaUrls}
                  originalSeedingUrl={originalSeedingUrl}
                  mediaChanged={mediaChanged}
                  linkChanged={linkChanged}
                  setEditingSubmissionId={setEditingSubmissionId}
                  setIsEditFormOpen={setIsEditFormOpen}
                  setMediaUrls={setMediaUrls}
                  setSeedingUrl={setSeedingUrl}
                  setUploadingFiles={setUploadingFiles}
                  setOriginalMediaUrls={setOriginalMediaUrls}
                  setOriginalSeedingUrl={setOriginalSeedingUrl}
                  onViewMedia={handleViewMedia}
                  onApplicationUpdate={setApplication}
                />
                </div>

                {/* 문의처 — 파일 제출 아래, 잠금 영역 밖이라 항상 접근 가능해요 */}
                <ContactCard
                  contact={campaign?.contact}
                  shopName={campaign?.shop_name}
                  className="mt-4"
                />
              </div>
            </div>
          </div>

          {/* 하단 여백 */}
          <div className="pb-16" />
        </div>
      </main>

      <Footer />

      {/* 미디어 뷰어 모달 */}
      {viewingMedia && (
        <MediaViewerModal
          mediaUrls={viewingMedia.urls}
          initialIndex={viewingMedia.index}
          onClose={() => setViewingMedia(null)}
        />
      )}
    </div>
  )
}

/** 기존 라우트 wrapper (Route.useParams/useSearch → props 전달) */
function SubmitPage() {
  const { mallId, shopNo: shopNoStr, campaignId } = Route.useParams()
  const { phone, code } = Route.useSearch()
  return (
    <MissionSubmitContent
      mallId={mallId}
      shopNo={parseInt(shopNoStr, 10)}
      campaignId={campaignId}
      phoneFromUrl={phone}
      codeFromUrl={code}
    />
  )
}
